import React, { useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminClinicsListTemplate } from '@/components/templates';
import { MassagersFilterPanel, MassagersTableSection } from '@/components/organisms';
import {
  useAdminMassagersList,
  useAdminMassagersCsvDownload,
  type MassagerListParams,
} from '@/hooks/admin/useAdminMassagers';
import { getTenantSiteTypes } from '@/services/tenants';
import type { TenantSiteTypeItem } from '@/services/tenants';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import useAppToast from '@/hooks/useAppToast';

const DEFAULT_PER_PAGE = 20;

type SortField =
  | 'clinics.clinic_number'
  | 'clinic_informations.name'
  | 'massagers.serial_number'
  | 'massagers.name'
  | 'massagers.login_id';
type SortDir = 'ascend' | 'descend';
type SortOrderParam = 'asc' | 'desc';

type MassagerFilterState = {
  clinicNumber: string;
  clinicName: string;
  searchLoginId: string;
  searchName: string;
  siteTypes: string[];
};

const getFiltersFromSearchParams = (searchParams: URLSearchParams): MassagerFilterState => {
  const siteTypesFromArray = searchParams.getAll('site_types').filter(Boolean);
  const siteTypesFromCsv = (searchParams.get('site_types') ?? '')
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
  const siteTypes = siteTypesFromArray.length > 0 ? siteTypesFromArray : siteTypesFromCsv;

  return {
    clinicNumber: searchParams.get('clinic_number') ?? '',
    clinicName: searchParams.get('clinic_name') ?? '',
    searchLoginId: searchParams.get('search_login_id') ?? '',
    searchName: searchParams.get('search_name') ?? '',
    siteTypes,
  };
};

const getSortStateFromSearchParams = (
  searchParams: URLSearchParams
): { field: SortField; dir: SortDir } | null => {
  const sortBy = searchParams.get('sort_by');
  const order = searchParams.get('order');
  const isValidSortField = (value: string | null): value is SortField =>
    value === 'clinics.clinic_number' ||
    value === 'clinic_informations.name' ||
    value === 'massagers.serial_number' ||
    value === 'massagers.name' ||
    value === 'massagers.login_id';

  if (!isValidSortField(sortBy)) return null;
  if (order !== 'asc' && order !== 'desc') return null;
  return { field: sortBy, dir: order === 'asc' ? 'ascend' : 'descend' };
};

const getPageFromSearchParams = (searchParams: URLSearchParams): number => {
  const raw = Number(searchParams.get('page'));
  return Number.isInteger(raw) && raw >= 1 ? raw : 1;
};

const SiteAdminMassagersListPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { isLoading: isAuthLoading } = useSiteAdminAuth();
  const toast = useAppToast();

  const isSuperAdminRoute = pathname.startsWith('/super-admin/admin');

  const siteTypesQuery = useQuery({
    queryKey: ['tenants', 'site-types', isSuperAdminRoute ? 'all' : (site ?? '')],
    queryFn: () => getTenantSiteTypes(isSuperAdminRoute ? undefined : (site ?? undefined)),
    enabled: !isAuthLoading && !!site,
    staleTime: Infinity,
  });

  const tenantSiteTypeList: TenantSiteTypeItem[] = siteTypesQuery.data?.data?.site_types ?? [];

  const canUseSiteTypeFeatures = tenantSiteTypeList.length > 1;

  const tenants = tenantSiteTypeList;
  const tenantNameBySiteType = useMemo(() => {
    const map = new Map<string, string>();
    tenantSiteTypeList.forEach(t => {
      if (t.site_type) map.set(t.site_type, t.name);
    });
    return map;
  }, [tenantSiteTypeList]);

  useEffect(() => {
    if (siteTypesQuery.isError) {
      toast.error('サイト一覧の取得に失敗しました。');
    }
  }, [siteTypesQuery.isError, toast]);

  const initialFilters = getFiltersFromSearchParams(searchParams);
  const [filters, setFilters] = useState<MassagerFilterState>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<MassagerFilterState>(initialFilters);

  const [page, setPage] = useState(getPageFromSearchParams(searchParams));
  const [sortState, setSortState] = useState<{ field: SortField; dir: SortDir } | null>(
    getSortStateFromSearchParams(searchParams)
  );

  useEffect(() => {
    const next = getFiltersFromSearchParams(searchParams);
    const nextPage = getPageFromSearchParams(searchParams);
    const nextSortState = getSortStateFromSearchParams(searchParams);
    setFilters(next);
    setAppliedFilters(next);
    setPage(nextPage);
    setSortState(nextSortState);
  }, [searchParams]);

  const params: MassagerListParams = useMemo(
    () => ({
      clinic_number: appliedFilters.clinicNumber || undefined,
      clinic_name: appliedFilters.clinicName || undefined,
      site_types:
        canUseSiteTypeFeatures && appliedFilters.siteTypes.length > 0
          ? appliedFilters.siteTypes
          : undefined,
      search_login_id: appliedFilters.searchLoginId || undefined,
      search_name: appliedFilters.searchName || undefined,
      sort: sortState ? [sortState.field, sortState.dir === 'ascend' ? 'asc' : 'desc'] : undefined,
      page,
      per_page: DEFAULT_PER_PAGE,
    }),
    [appliedFilters, canUseSiteTypeFeatures, sortState, page]
  );

  const { data, isLoading } = useAdminMassagersList(site ?? null, params);
  const { downloadCsv, isDownloading } = useAdminMassagersCsvDownload();

  const csvParams: MassagerListParams = useMemo(
    () => ({
      clinic_number: appliedFilters.clinicNumber || undefined,
      clinic_name: appliedFilters.clinicName || undefined,
      site_types:
        canUseSiteTypeFeatures && appliedFilters.siteTypes.length > 0
          ? appliedFilters.siteTypes
          : undefined,
      search_login_id: appliedFilters.searchLoginId || undefined,
      search_name: appliedFilters.searchName || undefined,
      sort: sortState ? [sortState.field, sortState.dir === 'ascend' ? 'asc' : 'desc'] : undefined,
    }),
    [appliedFilters, canUseSiteTypeFeatures, sortState]
  );

  const handleExportCsv = () => {
    if (!site) return;
    downloadCsv(site, csvParams);
  };

  const rows = data?.data ?? [];
  const meta = data?.meta
    ? {
        current_page: data.meta.current_page,
        last_page: data.meta.last_page,
        per_page: data.meta.per_page,
        total: data.meta.total,
      }
    : { current_page: 1, last_page: 1, per_page: DEFAULT_PER_PAGE, total: 0 };

  const handleSearch = () => {
    if (!site) return;
    const nextParams = new URLSearchParams();
    if (filters.clinicNumber.trim()) nextParams.set('clinic_number', filters.clinicNumber.trim());
    if (filters.clinicName.trim()) nextParams.set('clinic_name', filters.clinicName.trim());
    if (filters.searchLoginId.trim())
      nextParams.set('search_login_id', filters.searchLoginId.trim());
    if (filters.searchName.trim()) nextParams.set('search_name', filters.searchName.trim());
    if (canUseSiteTypeFeatures) {
      filters.siteTypes
        .map(v => v.trim())
        .filter(Boolean)
        .forEach(v => nextParams.append('site_types', v));
    }
    nextParams.set('page', '1');
    if (sortState) {
      nextParams.set('sort_by', sortState.field);
      nextParams.set('order', sortState.dir === 'ascend' ? 'asc' : 'desc');
    }

    flushSync(() => {
      setSearchParams(nextParams);
      setAppliedFilters(filters);
      setPage(1);
    });
    void queryClient.refetchQueries({
      queryKey: ['admin', 'massagers', site ?? ''],
      type: 'active',
    });
  };

  const setSort = (field: SortField | null, dir: SortDir | null) => {
    const nextParams = new URLSearchParams(searchParams);

    if (!field || !dir) {
      nextParams.delete('sort_by');
      nextParams.delete('order');
      nextParams.set('page', '1');
      setSearchParams(nextParams);
      return;
    }

    nextParams.set('sort_by', field);
    nextParams.set('order', (dir === 'ascend' ? 'asc' : 'desc') as SortOrderParam);
    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const handleChangePage = (nextPage: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('page', String(nextPage));
    setSearchParams(nextParams);
  };

  return (
    <>
      <AdminClinicsListTemplate
        breadcrumbItems={[{ title: '施術者CSV' }]}
        title="施術者CSV"
        filter={
          <MassagersFilterPanel
            tenants={tenants}
            filters={filters}
            onChangeFilters={next => setFilters(prev => ({ ...prev, ...next }))}
            onSearch={handleSearch}
            onCsv={handleExportCsv}
            csvLoading={isDownloading}
          />
        }
        table={
          <MassagersTableSection
            site={site!}
            rows={rows}
            loading={isLoading}
            meta={meta}
            defaultPerPage={DEFAULT_PER_PAGE}
            canUseSiteTypeFeatures={canUseSiteTypeFeatures}
            tenantNameBySiteType={tenantNameBySiteType}
            sortState={sortState}
            onSort={setSort}
            onChangePage={handleChangePage}
          />
        }
      />
    </>
  );
};

export default SiteAdminMassagersListPage;
